import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetchRecords,
} from '../services/InvoiceItemsService';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// FNP-918: the data layer for the invoice-items list.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was `['invoiceItems', invoiceId, currentToken]` — a bare string plus a page
// token, which makes the cache page-shaped rather than list-shaped: every page
// step wrote a new cache entry and nothing could invalidate the list as a whole.
//
// ⚠️ This module is INVOICE-SCOPED. `invoiceId` is part of every key, because
// two invoices' item lists are different lists — sharing a key would serve one
// invoice's items for another.
export const invoiceItemsKeys = {
    all: ['invoice-items'] as const,
    lists: () => [...invoiceItemsKeys.all, 'list'] as const,
    list: (invoiceId: string, filters: Record<string, string> = {}) =>
        [...invoiceItemsKeys.lists(), invoiceId, {filters}] as const,
    details: () => [...invoiceItemsKeys.all, 'detail'] as const,
    detail: (invoiceId: string, id: string) =>
        [...invoiceItemsKeys.details(), invoiceId, id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + the app-local `usePagination` token stack, which reads
 * as compliant (a query hook, page controls, forward/back buttons) while being
 * a page-at-a-time model the shared footer cannot describe. `useInfiniteQuery`
 * concatenates pages instead, so the client holds a growing loaded window and
 * the footer can honestly say how much of it it has seen.
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever.
 *
 * Every filter is in the queryKey (§9): omit one and changing it silently
 * serves the previous filter's cache.
 */
export const useInvoiceItemsInfiniteQuery = (
    invoiceId: string,
    filters: Record<string, string> = {},
) => {
    return useInfiniteQuery({
        queryKey: invoiceItemsKeys.list(invoiceId, filters),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords(
                {
                    ...filters,
                    invoice: invoiceId,
                },
                PAGE_SIZE,
                pageParam ?? undefined,
            ),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
        // Only meaningful once we know which invoice's items to fetch.
        enabled: !!invoiceId,
    });
};

/**
 * Batch delete. The whole module key is invalidated on success so the table
 * reflects the removal without a manual reload (§8).
 */
export const useDeleteInvoiceItemsMutation = (invoiceId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ids: string[]) =>
            Promise.all(ids.map(id => deleteRecord(invoiceId, id))),
        onSuccess: (_data, ids) => {
            notifySuccess(
                ids.length === 1
                    ? 'Invoice item deleted'
                    : `${ids.length} invoice items deleted`,
            );
            queryClient.invalidateQueries({queryKey: invoiceItemsKeys.all});
        },
        onError: notifyError,
    });
};
